import React from 'react';
import { motion } from 'framer-motion';
import { Star } from 'lucide-react';
interface Testimonial {
  initials: string;
  name: string;
  location: string;
  text: string;
  color: string;
  pack?: string;
}
interface TestimonialsProps {
  testimonials: Testimonial[];
  title?: string;
  className?: string;
}
export function Testimonials({
  testimonials,
  title,
  className = ''
}: TestimonialsProps) {
  return <section className={`py-16 bg-base ${className}`} data-id="element-304">
      <div className="container mx-auto px-4" data-id="element-305">
        {title && <h2 className="font-bold text-center mb-12 text-forest-dark text-[18px]" data-id="element-306">
            {title}
          </h2>}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto" data-id="element-307">
          {testimonials.map((t, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.1
        }} className="bg-white p-6 rounded-2xl shadow-sm border border-sand flex flex-col h-full" data-id="element-308">
              <div className="flex items-center gap-1 mb-4" data-id="element-309">
                {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 text-gold fill-gold" data-id="element-310" />)}
              </div>

              <p className="text-muted italic mb-6 flex-grow" data-id="element-311">"{t.text}"</p>

              <div className="flex items-center gap-3 mt-auto pt-4 border-t border-sand" data-id="element-312">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm ${t.color}`} data-id="element-313">
                  {t.initials}
                </div>
                <div data-id="element-314">
                  <p className="font-bold text-forest-dark text-sm" data-id="element-315">{t.name}</p>
                  <p className="text-xs text-muted" data-id="element-316">{t.location}</p>
                  {t.pack && <p className="text-xs font-medium text-earth mt-0.5" data-id="element-317">
                      {t.pack}
                    </p>}
                </div>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}
export const initialTestimonials = [{
  initials: 'MG',
  name: 'María G.',
  location: 'Docente de Técnicas Mineras, San Juan',
  text: 'En una tarde tenía más material contextualizado en minería argentina que en toda mi carrera docente. Ahora mis alumnos me preguntan sobre el litio de Jujuy y Josemaría, y tengo respuesta fundamentada para todo.',
  color: 'bg-forest'
}, {
  initials: 'RT',
  name: 'Rodrigo T.',
  location: 'Docente de Minería, Santa Cruz',
  text: 'Mis alumnos por primera vez entendieron la diferencia entre un recurso inferido y una reserva probada, y analizaron el conflicto de Navidad en Chubut con argumentos técnicos reales. El nivel de participación se multiplicó.',
  color: 'bg-earth'
}, {
  initials: 'SP',
  name: 'Sandra P.',
  location: 'Profesora de Explotación Minera, Jujuy',
  text: 'Los ejercicios de balance metalúrgico con datos de Cerro Negro son exactamente lo que mis alumnos necesitaban: minería técnica real con contexto argentino. Cambió todo.',
  color: 'bg-gold-dark'
}];
export const moreTestimonials = [{
  initials: 'FM',
  name: 'Federico M.',
  location: '🏙️ Buenos Aires',
  pack: 'Megapack Minería',
  text: 'Con el Megapack en una tarde tenía el argumento pedagógico para explicar por qué Argentina usa salmueras de salar y Australia usa espodumena. Mis alumnos empezaron a preguntar sobre Josemaría como si fuera algo suyo. Porque lo es.',
  color: 'bg-forest'
}, {
  initials: 'RV',
  name: 'Rosa V.',
  location: '🏙️ San Juan',
  pack: 'Megapack Minería',
  text: 'Cuando explicás que el derrame de Veladero en 2015 fue el resultado de una válvula sin mantenimiento, y aplicás los 5 Por qué con los alumnos, empieza un debate técnico-ciudadano que ninguna otra materia genera.',
  color: 'bg-earth'
}, {
  initials: 'GN',
  name: 'Gabriela N.',
  location: '🏙️ Chubut',
  pack: 'Megapack Minería',
  text: 'Cuando mis alumnos descubren que Navidad es el mayor depósito de plata sin explotar del mundo y que una ley provincial lo frenó 15 años, empieza una conversación sobre soberanía, desarrollo y ambiente que ningún libro de texto puede provocar.',
  color: 'bg-gold-dark'
}, {
  initials: 'AS',
  name: 'Alejandro S.',
  location: '🏙️ Catamarca',
  pack: 'Megapack Minería',
  text: 'Cerré la planificación anual en dos horas. Antes me llevaba semanas. Ahora tengo todo el año organizado con Josemaría, Cauchari-Olaroz y Cerro Negro integrados en cada unidad.',
  color: 'bg-forest-soft'
}, {
  initials: 'SM',
  name: 'Sofía M.',
  location: '🏙️ Neuquén',
  pack: 'Megapack + Upsell Premium',
  text: 'Cuando les digo a mis alumnos que el litio argentino está en el centro de la disputa geopolítica entre China, EEUU y Europa, y que el técnico minero del futuro va a supervisar camiones autónomos desde una sala de control, les estoy enseñando el mundo en el que van a trabajar.',
  color: 'bg-earth-light'
}];